import CustomFields from "./inputComponents/CustomFields"
import BarcodeField from "./inputComponents/BarcodeField"
import RemoteControlButtons from "./inputComponents/RemoteControlButtons"
import { Grid } from "@mui/material"
import RemoteControlPassive from "./inputComponents/RemoteControlPassive"
import Commodities from "./inputComponents/Commodities"
import PhotoAttachment from "./inputComponents/PhotoAttachment"
import { useEffect, useState, useRef } from "react"
import { useFeatureFlags } from "../../../FeatureFlagsContext"
import { MY_HOST } from "../../../ts/Common"

const styles = {
    customFields: {
        overflow: "auto",
        maxHeight: {
            xs: '50vh',
            sm: '60vh',
            md: '75vh'
        }
    },
}

export default function Inputs(props: any) {
    const { customFields, customFieldDropDownValues, customFieldSubmitValues, setCustomFieldSubmitValues, setCustomFieldSubmitValuesEmpty, customFieldsLoaded, barcodeValue,
        setBarcodeValue, machineActive, setMachineActive, settings, showMachineSettings, settingsLoaded, machineCommand, machineScan, scanButtonPressed, setScanButtonPressed, disableScanButton, setDisableScanButton, cargoResponses,
        setMachineCommand, checkCustomFieldsReady, dimensionerMode, machine, setOpenForkliftModal, commodities, commoditiesLoaded, onFreightClassSelected, forklift, user, drawerOpen, mobileNavbarOpen, pendingBarcode, setPendingBarcode, clearPhotosSignal } = props
    const [scanOnEnter, setScanOnEnter] = useState<boolean>(false);
    const { featureFlags } = useFeatureFlags();
    const [aiOriginalValues, setAiOriginalValues] = useState<Map<string, any>>(new Map());
    const [aiLoading, setAiLoading] = useState<boolean>(false);
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (settingsLoaded) {
            setScanOnEnter(settings.Scanner?.AutoBarcodeEnabled?.value)
        }
    }, [settingsLoaded, settings, showMachineSettings])

    const applyAiResults = (results: Record<string, string>) => {
        const copy = JSON.parse(JSON.stringify(customFieldSubmitValues));
        const newPopulated = new Map<string, any>();
        Object.keys(results).forEach((key: string) => {
            const value: string = results[key];
            if (value === null || value === "") return;

            customFields.forEach((field: any, index: number) => {
                if (field.name.toLowerCase() === key.toLowerCase()) {
                    // For dropdowns, only apply if the value matches an available option
                    if (field.fieldType === 3) {
                        const match = customFieldDropDownValues.find(
                            (item: any) => item.customFieldID === field.customFieldID &&
                                item.value?.toLowerCase() === String(value).toLowerCase()
                        );
                        if (!match) return;
                        copy['CustomFields']['Field'][index]['Value'] = match.value;
                        copy['CustomFields']['Field'][index]['Ready'] = true;
                        newPopulated.set(field.name, match.value);
                    } else if (field.fieldType === 2) {
                        const boolValue = String(value).toLowerCase() === 'true';
                        copy['CustomFields']['Field'][index]['Value'] = boolValue;
                        copy['CustomFields']['Field'][index]['Ready'] = true;
                        newPopulated.set(field.name, boolValue);
                    } else {
                        copy['CustomFields']['Field'][index]['Value'] = value;
                        copy['CustomFields']['Field'][index]['Ready'] = true;
                        newPopulated.set(field.name, value);
                    }
                }
            });
        });
        setCustomFieldSubmitValues(copy);
        setAiOriginalValues(prev => new Map([...prev, ...newPopulated]));
    };

    const handleAiScan = async (files: File[]) => {
        if (!files.length) return;
        setAiLoading(true);
        
        const startTime = Date.now();
        const MAX_WAIT_MS = 30000; // 30 seconds
        let timeoutId: NodeJS.Timeout | null = null;

        try {
            const formData = new FormData();
            files.forEach((file) => formData.append('images', file));

            // Submit the job asynchronously
            const submitResponse = await fetch(`${MY_HOST}/api/Inference/AnalyzeCustomFields/Async`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });
            if (!submitResponse.ok) throw new Error(`Server error: ${submitResponse.status}`);
            const { token } = await submitResponse.json();

            // Poll for the result every 2 seconds
            await new Promise<void>((resolve, reject) => {
                const check = async () => {
                    try {
                        // Check if component is still mounted
                        if (!isMountedRef.current) {
                            reject(new Error('Component unmounted'));
                            return;
                        }

                        // Check if we've exceeded max wait time
                        if (Date.now() - startTime > MAX_WAIT_MS) {
                            reject(new Error('Inference timeout: exceeded 30 seconds'));
                            return;
                        }

                        const statusResponse = await fetch(`${MY_HOST}/api/Inference/Status/${token}`, {
                            credentials: 'include',
                        });
                        if (!statusResponse.ok) {
                            reject(new Error(`Status check failed: ${statusResponse.status}`));
                            return;
                        }
                        const statusData = await statusResponse.json();
                        if (statusData.status === 'completed') {
                            applyAiResults(statusData.result);
                            resolve();
                        } else if (statusData.status === 'failed') {
                            reject(new Error(statusData.error || 'Inference job failed'));
                        } else {
                            timeoutId = setTimeout(check, 2000);
                        }
                    } catch (e) {
                        reject(e);
                    }
                };
                check();
            });
        } catch (e: any) {
            if (isMountedRef.current) {
                console.error('AI scan failed:', e.message || 'Something went wrong');
            }
        } finally {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            if (isMountedRef.current) {
                setAiLoading(false);
            }
        }
    };

    const aiScanEnabled = Array.isArray(customFields) && customFields.some((field: any) => field.aiEnabled === true);

    return (
        <>
            <Grid item sx={styles.customFields}>
                {
                    dimensionerMode === "Passive" ?
                        <></>
                        :
                        <>
                            <CustomFields
                                customFields={customFields} customFieldDropDownValues={customFieldDropDownValues} checkCustomFieldsReady={checkCustomFieldsReady}
                                customFieldSubmitValues={customFieldSubmitValues} setCustomFieldSubmitValues={setCustomFieldSubmitValues}
                                setCustomFieldSubmitValuesEmpty={setCustomFieldSubmitValuesEmpty} customFieldsLoaded={customFieldsLoaded}
                                scanButtonPressed={scanButtonPressed} cargoResponses={cargoResponses} barcodeValue={barcodeValue}
                                aiOriginalValues={aiOriginalValues}
                            ></CustomFields>
                            <Commodities
                                commodities={commodities}
                                commoditiesLoaded={commoditiesLoaded}
                                onFreightClassSelected={onFreightClassSelected}
                            ></Commodities>
                            {featureFlags.AttachFile && (
                                <PhotoAttachment machine={machine} machineActive={machineActive} setMachineActive={setMachineActive} onAiScan={handleAiScan} aiLoading={aiLoading} aiScanEnabled={aiScanEnabled} clearPhotosSignal={clearPhotosSignal} />
                            )}
                        </>
                }
            </Grid >
            <Grid item>
                <Grid pb={{ xs: 20, sm: 20, md: 5 }}>
                    {
                        dimensionerMode === "Passive" ?
                            <>
                                {featureFlags.AttachFile && (
                                    <PhotoAttachment machine={machine} machineActive={machineActive} setMachineActive={setMachineActive} onAiScan={handleAiScan} aiLoading={aiLoading} aiScanEnabled={aiScanEnabled} clearPhotosSignal={clearPhotosSignal} />
                                )}
                                <Grid mt={2}>
                                    <RemoteControlPassive machine={machine} cargoResponses={cargoResponses} forklift={forklift}
                                        setOpenForkliftModal={setOpenForkliftModal} user={user} pendingBarcode={pendingBarcode} setPendingBarcode={setPendingBarcode} scanOnEnter={scanOnEnter}></RemoteControlPassive>
                                </Grid>
                            </>
                            :
                            <>
                                <BarcodeField
                                    barcodeValue={barcodeValue}
                                    setBarcodeValue={setBarcodeValue}
                                    machineActive={machineActive}
                                    settings={settings}
                                    showMachineSettings={showMachineSettings}
                                    settingsLoaded={settingsLoaded}
                                    machineCommand={machineCommand}
                                    machineScan={machineScan}
                                    scanButtonPressed={scanButtonPressed}
                                    setScanButtonPressed={setScanButtonPressed}
                                    drawerOpen={drawerOpen}
                                    mobileNavbarOpen={mobileNavbarOpen}
                                    scanOnEnter={scanOnEnter}
                                ></BarcodeField>
                                <RemoteControlButtons machineScan={machineScan}
                                    machineActive={machineActive} disableScanButton={disableScanButton} setDisableScanButton={setDisableScanButton} cargoResponses={cargoResponses} settings={settings} settingsLoaded={settingsLoaded}
                                    showMachineSettings={showMachineSettings} machineCommand={machineCommand} setMachineCommand={setMachineCommand} scanButtonPressed={scanButtonPressed} setScanButtonPressed={setScanButtonPressed}>
                                </RemoteControlButtons>
                            </>
                    }
                </Grid>
            </Grid>
        </>
    )
}